// «Genera le ancore dei capitoli»: dà un §id ai titoli che non ce l'hanno.
//
// Un id lo si può citare solo se esiste, e scriverli a mano su un manuale di
// venti capitoli è il lavoro che si rimanda — finché ci si accorge di non poter
// citare metà documento. Qui li deriva dal titolo, con la convenzione slug.
//
// Tocca solo `#` e `##`, e solo dove l'ancora manca: un id già scritto è una
// decisione presa, magari citata altrove, e riscriverlo romperebbe le citazioni
// esistenti senza dirlo a nessuno.

const TITOLO = /^(\s{0,3})(#{1,2})\s+(.*?)\s*$/;
const ANCORA_IN_CODA = /§[A-Za-z][A-Za-z0-9_.-]*\s*$/;
const RECINTO = /^\s*(```|~~~)/;


export function slug(titolo: string): string
{
    const senzaAccenti = titolo.normalize("NFD").replace(/[̀-ͯ]/g, "");

    const pulito = senzaAccenti
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    // L'engine vuole che un identificatore cominci per lettera. Un capitolo
    // intitolato «403 e 404» darebbe `403-e-404`, rifiutato — ma tagliare via
    // il numero darebbe `e-404`, che perde proprio la parola che conta. Meglio
    // una `c` di capitolo davanti: `c-403-e-404`.
    if (pulito === "") { return "capitolo"; }

    return /^[a-z]/.test(pulito) ? pulito : `c-${pulito}`;
}


export interface Esito
{
    testo: string;
    /** Le ancore aggiunte, in ordine: servono a dire cosa è successo. */
    aggiunte: { titolo: string; id: string }[];
}


export function generaAncore(testo: string): Esito
{
    const righe = testo.split("\n");
    const aggiunte: Esito["aggiunte"] = [];

    // Gli id già in uso, presi dai titoli: il nuovo non può coincidere con uno
    // esistente, o la citazione diventa ambigua.
    const presi = new Set<string>();

    for (const riga of righe)
    {
        const gia = ANCORA_IN_CODA.exec(riga);

        if (gia !== null && TITOLO.test(riga)) { presi.add(gia[0].trim().slice(1)); }
    }

    let recinto: string | null = null;

    const uscita = righe.map((riga) =>
    {
        const apertura = RECINTO.exec(riga)?.[1];

        if (recinto !== null)
        {
            if (apertura === recinto) { recinto = null; }
            return riga;
        }

        if (apertura !== undefined) { recinto = apertura; return riga; }

        const m = TITOLO.exec(riga);

        if (m === null || ANCORA_IN_CODA.test(riga)) { return riga; }

        const titolo = m[3] ?? "";

        if (titolo === "") { return riga; }

        const base = slug(titolo);
        let id = base;
        let n = 2;

        while (presi.has(id)) { id = `${base}-${n}`; n++; }

        presi.add(id);
        aggiunte.push({ titolo, id });

        return `${m[1] ?? ""}${m[2] ?? ""} ${titolo} §${id}`;
    });

    return { testo: uscita.join("\n"), aggiunte };
}
